import type { LottieRefCurrentProps } from "lottie-react";

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  getCachedLottieData,
  getCachedLottieFrame,
  getLottieData,
  getTenantLottiePath,
  setCachedLottieFrame,
} from "./lottie-cache";

import { CONSTANTS } from "@/config/constants";
import { useAppTheme } from "@/hooks/use-app-theme";
import { useLogo } from "@/hooks/use-logo";

const Lottie = lazy(() => import("lottie-react"));

const LOTTIE_SPEED = 1.25;

export const TenantLogoOrLottie = () => {
  const { full } = useLogo();
  const theme = useAppTheme();
  const lottiePath = getTenantLottiePath(
    CONSTANTS.APP_SLUG,
    theme === "light" ? "light" : "dark",
  );

  const [animationData, setAnimationData] = useState<unknown | null>(() =>
    lottiePath ? getCachedLottieData(lottiePath) : null,
  );

  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (!lottiePath) return;

    const cached = getCachedLottieData(lottiePath);

    if (cached) {
      setAnimationData(cached);

      return;
    }

    let cancelled = false;

    setAnimationData(null);

    getLottieData(lottiePath)
      .then((data) => {
        if (!cancelled) setAnimationData(data);
      })
      .catch(() => {
        // Silent fallback - keep showing <img>.
      });

    return () => {
      cancelled = true;
    };
  }, [lottiePath]);

  useEffect(() => {
    if (!lottiePath) return;

    return () => {
      const frame = lottieRef.current?.animationItem?.currentFrame;

      if (typeof frame === "number") {
        setCachedLottieFrame(lottiePath, frame);
      }
    };
  }, [lottiePath]);

  const handleDOMLoaded = useCallback(() => {
    if (!lottiePath) return;

    lottieRef.current?.setSpeed(LOTTIE_SPEED);
    const saved = getCachedLottieFrame(lottiePath);

    if (saved > 0) {
      lottieRef.current?.goToAndPlay(saved, true);
    }
  }, [lottiePath]);

  const fallbackImage = <img alt="" className="h-[50px] w-auto" src={full} />;

  if (!lottiePath || !animationData) {
    return fallbackImage;
  }

  return (
    <Suspense fallback={fallbackImage}>
      <Lottie
        loop
        animationData={animationData}
        className=" w-[50vw]"
        lottieRef={lottieRef}
        onDOMLoaded={handleDOMLoaded}
      />
    </Suspense>
  );
};
